import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Check, X } from 'lucide-react';
import { cn } from '../../lib/cn';
import { CHANGES_SAVED, TOAST_DURATION_MS, ToastContext, type ToastApi } from './toast';

interface Shown {
  /** A new one for every show(), so the same text shown twice is announced twice. */
  id: number;
  message: string;
}

/**
 * The app's one save toast (spec "Save confirmation toast"), mounted once in App.tsx beside the
 * DialogProvider. `useToast().show()` puts a toast up; a second one replaces the first.
 *
 * - Where: centred at the bottom of the window, above the sticky Save footer when a page has one (the
 *   footer keeps its height in `--sticky-save-footer-h`, STICKY_FOOTER_HEIGHT_VAR) and, below `md`,
 *   above the phone's bottom nav as well (64px plus the home-indicator inset, BottomNav's ABOVE_BAR).
 * - Layer: z-[48], over the bottom nav and the phone's top bar (z-[45], z-[46]) and under every modal
 *   and Select list (z-50).
 * - Look: dark in both themes (the chrome bar's fixed near-black) with white text and a check icon.
 * - Behaviour: it dismisses itself after TOAST_DURATION_MS (about 4.5 s), held while the pointer is
 *   over it or the focus is in it, so a keyboard user on its close button never loses it mid-reach.
 *   It never takes the focus.
 * - Screen readers: the `role="status"` / `aria-live="polite"` region is always in the document and
 *   only its text changes (a live region added with its text already in it is often not read out).
 *   It holds the message alone: the close button, "Dismiss notification", sits beside it.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Shown | null>(null);
  const nextId = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The pointer is over the toast or the focus is in it: no countdown until it leaves.
  const held = useRef(false);

  const stopTimer = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  }, []);
  const startTimer = useCallback(() => {
    stopTimer();
    timer.current = setTimeout(() => {
      timer.current = null;
      setToast(null);
    }, TOAST_DURATION_MS);
  }, [stopTimer]);

  const show = useCallback(
    (message: string = CHANGES_SAVED) => {
      nextId.current += 1;
      setToast({ id: nextId.current, message });
      if (!held.current) startTimer();
    },
    [startTimer],
  );
  const dismiss = useCallback(() => {
    stopTimer();
    // The close button goes with the toast, and a removed element sends no blur: let go here.
    held.current = false;
    setToast(null);
  }, [stopTimer]);

  useEffect(() => stopTimer, [stopTimer]);

  const api = useMemo<ToastApi>(() => ({ show, dismiss }), [show, dismiss]);

  const hold = () => {
    held.current = true;
    stopTimer();
  };
  const release = () => {
    held.current = false;
    if (toast) startTimer();
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-[calc(4.75rem+env(safe-area-inset-bottom)+var(--sticky-save-footer-h,0rem))] z-[48] flex justify-center px-4 md:bottom-[calc(1rem+var(--sticky-save-footer-h,0rem))]">
        <div
          data-toast=""
          className={cn(
            'flex min-w-0 items-center gap-3',
            toast &&
              'pointer-events-auto max-w-md rounded-xl border border-chrome-bar-line bg-chrome-bar py-2 pl-4 pr-2 text-white shadow-panel motion-safe:animate-menu-in',
          )}
          onMouseEnter={hold}
          onMouseLeave={release}
          onFocus={hold}
          onBlur={release}
        >
          <div role="status" aria-live="polite" className="flex min-w-0 items-center gap-2.5 text-sm font-medium">
            {toast && (
              <>
                <Check className="h-4 w-4 shrink-0 text-success-dark" strokeWidth={2.5} aria-hidden />
                <span key={toast.id}>{toast.message}</span>
              </>
            )}
          </div>
          {toast && (
            <button
              type="button"
              aria-label="Dismiss notification"
              onClick={dismiss}
              className="focus-ring flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-chrome-muted transition-colors hover:bg-chrome-hover hover:text-white"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          )}
        </div>
      </div>
    </ToastContext.Provider>
  );
}
